import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [searchParams] = useSearchParams();
  const showQuote = searchParams.get('quote') === 'true';
  const preselectedService = searchParams.get('service') || '';

  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service_type: preselectedService || 'packaging',
    requirements: '',
    quantity: '',
    timeline: '',
  });

  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [isQuoteSubmitting, setIsQuoteSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [quoteSuccess, setQuoteSuccess] = useState(false);

  useEffect(() => {
    if (preselectedService) {
      setQuoteForm(prev => ({ ...prev, service_type: preselectedService }));
    }
  }, [preselectedService]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsContactSubmitting(true);

    try {
      await axios.post(`${API}/contact`, contactForm);
      setContactSuccess(true);
      setContactForm({ name: '', email: '', phone: '', message: '' });
      toast.success('Message sent successfully! We will get back to you soon.');
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsContactSubmitting(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsQuoteSubmitting(true);

    try {
      await axios.post(`${API}/quote`, quoteForm);
      setQuoteSuccess(true);
      setQuoteForm({
        name: '',
        email: '',
        phone: '',
        company: '',
        service_type: 'packaging',
        requirements: '',
        quantity: '',
        timeline: '',
      });
      toast.success('Quote request submitted! We will contact you shortly.');
    } catch (error) {
      console.error('Quote form error:', error);
      toast.error('Failed to submit quote request. Please try again.');
    } finally {
      setIsQuoteSubmitting(false);
    }
  };

  const offices = [
    {
      name: 'India Office (Headquarters)',
      company: 'Aumkar International',
      address: 'Dundigal, Hyderabad, Telangana, India',
      contacts: [
        { name: 'Ashok Poojary', role: 'Managing Director', phone: '+91 90362 40229' },
        { name: 'M Laxmipathi', role: 'Managing Director', phone: '+91 62817 56651' },
      ],
    },
    {
      name: 'Oman Office',
      company: 'Remas Cleaning & Goodluck Laundry',
      address: 'Muscat, Oman',
      contacts: [
        { name: 'Mamon Chowdhury', role: 'Oman Partner & Vice Director', phone: '+968 9490 0565' },
      ],
    },
  ];

  return (
    <div data-testid="contact-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-blue-400 mb-4">
            Contact Us
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-6">
            Let's <span className="text-blue-400">Connect</span>
          </h1>
          <p className="text-slate-300 max-w-2xl mx-auto text-lg">
            Have a question or need a quote? Reach out to us and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 mb-8">
                Our Offices
              </h2>
              <div className="space-y-6">
                {offices.map((office, index) => (
                  <Card key={index} className="border-slate-200">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-5 h-5 text-blue-700" />
                        </div>
                        <div>
                          <h3 className="font-bold text-slate-900 mb-1">{office.name}</h3>
                          <p className="text-sm text-blue-700 mb-2">{office.company}</p>
                          <p className="text-slate-600 text-sm mb-4">{office.address}</p>
                          {office.contacts.map((contact, i) => (
                            <div key={i} className="flex items-center gap-2 mb-2">
                              <Phone className="w-4 h-4 text-slate-400" />
                              <div>
                                <span className="text-sm text-slate-900 font-medium">{contact.name}</span>
                                <span className="text-sm text-slate-500"> - {contact.role}</span>
                                <a
                                  href={`tel:${contact.phone.replace(/\s/g, '')}`}
                                  className="block text-sm text-blue-700 hover:underline"
                                >
                                  {contact.phone}
                                </a>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Additional Info */}
              <div className="mt-8 space-y-4">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-blue-700" />
                  <a href="mailto:aumkarprivatelimited@gmail.com" className="text-slate-700 hover:text-blue-700">
                    aumkarprivatelimited@gmail.com
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-blue-700" />
                  <span className="text-slate-700">Mon - Sat: 9:00 AM - 6:00 PM</span>
                </div>
              </div>

              {/* Map */}
              <div className="mt-8">
                <h3 className="font-bold text-slate-900 mb-4">Find Us</h3>
                <div className="map-container">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d30438.85183682024!2d78.38076173476562!3d17.573852100000005!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb8d5f9c7a53ef%3A0x7e8a1cbf05d0fe28!2sDundigal%2C%20Telangana!5e0!3m2!1sen!2sin!4v1699999999999!5m2!1sen!2sin"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="AUMKAR Location"
                  />
                </div>
              </div>
            </div>

            {/* Forms */}
            <div>
              {/* Quote Form */}
              {(showQuote || preselectedService) && (
                <Card className="border-slate-200 mb-8 form-glow" data-testid="quote-form-card">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold">Request a Quote</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {quoteSuccess ? (
                      <div className="text-center py-8">
                        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                        <h3 className="text-xl font-bold text-slate-900 mb-2">Quote Request Submitted!</h3>
                        <p className="text-slate-600 mb-4">We'll get back to you with a detailed quote soon.</p>
                        <Button
                          onClick={() => setQuoteSuccess(false)}
                          variant="outline"
                          className="rounded-full"
                        >
                          Submit Another Request
                        </Button>
                      </div>
                    ) : (
                      <form onSubmit={handleQuoteSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="quote-name">Full Name *</Label>
                            <Input
                              id="quote-name"
                              data-testid="quote-name-input"
                              required
                              value={quoteForm.name}
                              onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                              placeholder="Your name"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="quote-email">Email *</Label>
                            <Input
                              id="quote-email"
                              data-testid="quote-email-input"
                              type="email"
                              required
                              value={quoteForm.email}
                              onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                              placeholder="your@email.com"
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="quote-phone">Phone *</Label>
                            <Input
                              id="quote-phone"
                              data-testid="quote-phone-input"
                              required
                              value={quoteForm.phone}
                              onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                              placeholder="+91 XXXXX XXXXX"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="quote-company">Company</Label>
                            <Input
                              id="quote-company"
                              data-testid="quote-company-input"
                              value={quoteForm.company}
                              onChange={(e) => setQuoteForm({ ...quoteForm, company: e.target.value })}
                              placeholder="Your company name"
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="quote-service">Service Type *</Label>
                          <Select
                            value={quoteForm.service_type}
                            onValueChange={(value) => setQuoteForm({ ...quoteForm, service_type: value })}
                          >
                            <SelectTrigger data-testid="quote-service-select" className="mt-1">
                              <SelectValue placeholder="Select a service" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="packaging">Packaging Manufacturing</SelectItem>
                              <SelectItem value="import-export">Import & Export / Shipping Clearance</SelectItem>
                              <SelectItem value="cleaning">Cleaning Services</SelectItem>
                              <SelectItem value="laundry">Laundry Services</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="quote-requirements">Requirements *</Label>
                          <Textarea
                            id="quote-requirements"
                            data-testid="quote-requirements-input"
                            required
                            value={quoteForm.requirements}
                            onChange={(e) => setQuoteForm({ ...quoteForm, requirements: e.target.value })}
                            placeholder="Describe your requirements in detail..."
                            className="mt-1 min-h-[100px]"
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="quote-quantity">Quantity/Volume</Label>
                            <Input
                              id="quote-quantity"
                              data-testid="quote-quantity-input"
                              value={quoteForm.quantity}
                              onChange={(e) => setQuoteForm({ ...quoteForm, quantity: e.target.value })}
                              placeholder="e.g., 1000 boxes"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="quote-timeline">Timeline</Label>
                            <Input
                              id="quote-timeline"
                              data-testid="quote-timeline-input"
                              value={quoteForm.timeline}
                              onChange={(e) => setQuoteForm({ ...quoteForm, timeline: e.target.value })}
                              placeholder="e.g., 2 weeks"
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <Button
                          type="submit"
                          data-testid="quote-submit-btn"
                          className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-full"
                          disabled={isQuoteSubmitting}
                        >
                          {isQuoteSubmitting ? (
                            'Submitting...'
                          ) : (
                            <>
                              Submit Quote Request
                              <Send className="ml-2 w-4 h-4" />
                            </>
                          )}
                        </Button>
                      </form>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Contact Form */}
              <Card className="border-slate-200" data-testid="contact-form-card">
                <CardHeader>
                  <CardTitle className="text-xl font-bold">Send Us a Message</CardTitle>
                </CardHeader>
                <CardContent>
                  {contactSuccess ? (
                    <div className="text-center py-8">
                      <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                      <h3 className="text-xl font-bold text-slate-900 mb-2">Message Sent!</h3>
                      <p className="text-slate-600 mb-4">Thank you for contacting us. We'll respond soon.</p>
                      <Button
                        onClick={() => setContactSuccess(false)}
                        variant="outline"
                        className="rounded-full"
                      >
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleContactSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="contact-name">Full Name *</Label>
                        <Input
                          id="contact-name"
                          data-testid="contact-name-input"
                          required
                          value={contactForm.name}
                          onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                          placeholder="Your name"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact-email">Email *</Label>
                        <Input
                          id="contact-email"
                          data-testid="contact-email-input"
                          type="email"
                          required
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          placeholder="your@email.com"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact-phone">Phone</Label>
                        <Input
                          id="contact-phone"
                          data-testid="contact-phone-input"
                          value={contactForm.phone}
                          onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                          placeholder="+91 XXXXX XXXXX"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact-message">Message *</Label>
                        <Textarea
                          id="contact-message"
                          data-testid="contact-message-input"
                          required
                          value={contactForm.message}
                          onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                          placeholder="How can we help you?"
                          className="mt-1 min-h-[120px]"
                        />
                      </div>
                      <Button
                        type="submit"
                        data-testid="contact-submit-btn"
                        className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-full"
                        disabled={isContactSubmitting}
                      >
                        {isContactSubmitting ? (
                          'Sending...'
                        ) : (
                          <>
                            Send Message
                            <Send className="ml-2 w-4 h-4" />
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
